import React, { Component, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Redirect } from 'react-router-dom'
import gql from 'graphql-tag'
import { Error404 } from './ErrorPage'
import { Loading, LoadingError } from './LoadingFork'
import { useApolloClient, useQuery } from "@apollo/client"
import { getNewBrandingKey } from '../lib/redirects'
import { activateInstallation, releaseInstallation } from '../lib/currentInstallation'

const InstallationContext = createContext({
	installation: null,
	facility: null,
	setFacility: () => {},
	updateFacility: (_partial) => {}
});

const installationByBrandingKeyQuery = gql`
	query InstallationByBrandingKey($brandingKey: String!) {
		installationByBrandingKey(brandingKey: $brandingKey) {
			installation {
				id
				name
				brandingKey
				timeZoneName
				serviceFeePercent
				status
				currency
				hasPublicConsole
				publicConsoleSlug
				createdAt
			}
			facility {
				id
				payment_method
				gates {
					id
				}
				city {
					id
					country
				}
				featureFacilityProviderConfigurations {
					id
					configuration
					feature {
						id
						name
					}
					provider {
						id
						name
					}
					provider {
						name
					}
				}
			}
		}
	}
`;

export function InstallationProvider({ match, location, children }) {
	// Rebranded hotels: forward bookmarked /old-key/... URLs to /new-key/...
	const legacyKey = match.params.brandingKey;
	const newKey = getNewBrandingKey(legacyKey);

	const client = useApolloClient();
	const { data, loading, error } = useQuery(installationByBrandingKeyQuery, {
		variables: { brandingKey: legacyKey },
		skip: Boolean(newKey),
	});

	const installation = data?.installationByBrandingKey?.installation;
	const facility = data?.installationByBrandingKey?.facility;

	const [facilityState, setFacility] = useState(facility);

	useEffect(() => {
		if (facility) setFacility(facility);
	}, [facility]);

	const value = useMemo(() => ({
		installation,
		facility: facilityState,
		setFacility,
		updateFacility: (partial) =>
			setFacility((prev) => (prev ? { ...prev, ...partial } : partial)),
	}), [installation, facilityState]);

	const ownerRef = useRef(null);
	useEffect(() => () => releaseInstallation(ownerRef), []);

	if (newKey) {
		const rest = location.pathname.slice(`/${legacyKey}`.length);
		return <Redirect to={`/${newKey}${rest}${location.search || ''}`} />;
	}

	if (loading) return <Loading />;
	if (error) return <LoadingError error={error} />;
	if (!installation) return <Error404 />;

	/**
	 * Set during render, not in an effect: children's effects run before ours,
	 * so their first queries would otherwise go out with the previous hotel's
	 * x-installation-id. Keyed by id so a hotel switch remounts the pages and
	 * they refetch instead of reading the previous hotel's cached results.
	 */
	activateInstallation(installation.id, client.cache, ownerRef);

	return (
		<InstallationContext.Provider key={installation.id} value={value}>
			{children}
		</InstallationContext.Provider>
	);
}

export function useInstallation() {
	return useContext(InstallationContext);
}

export function withInstallation(WrappedComponent) {
	return class WithInstallation extends Component {
		static displayName = `WithInstallation(${WrappedComponent.displayName || WrappedComponent.name})`;
		render() {
			return (
				<InstallationContext.Consumer>
					{({ installation, facility, setFacility, updateFacility }) => (
						<WrappedComponent
							{...this.props}
							installation={installation}
							facility={facility}
							setFacility={setFacility}
							updateFacility={updateFacility}
						/>
					)}
				</InstallationContext.Consumer>
			);
		}
	};
}
